// src/components/ManageAccessModal.js
import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  makeStyles,
  Button,
  Menu,
  MenuItem,
  CircularProgress,
  Backdrop,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import VisibilityIcon from "@material-ui/icons/Visibility";
import EditIcon from "@material-ui/icons/Edit";
import LockIcon from "@material-ui/icons/Lock";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import DocuwareTemplateService from "../../services/DocuwareTemplateService";
import notify from "../../utils/notifier";

const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    width: "30%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#F6F6F6",
  },
  titleBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#fff",
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  content: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
  },
  section: {
    marginBottom: theme.spacing(2),
  },
  tenantItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#fff",
    padding: "12px 16px",
    marginBottom: theme.spacing(1),
    borderRadius: 8,
    border: "1px solid #e0e0e0",
  },
  accessButtonContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  iconButton: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    backgroundColor: "#E8E8E8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    border: "none",
    padding: 0,
    "&:hover": {
      backgroundColor: "#D0D0D0",
    },
  },
  iconButtonActive: {
    backgroundColor: "#90CAF9",
    "&:hover": {
      backgroundColor: "#64B5F6",
    },
  },
  dropdownButton: {
    minWidth: 24,
    width: 24,
    height: 24,
    padding: 0,
  },
  footer: {
    borderTop: "1px solid #ddd",
    padding: theme.spacing(2),
    display: "flex",
    justifyContent: "flex-end",
    backgroundColor: "#fff",
  },
  updateBtn: {
    borderRadius: "999px",
    padding: "10px 32px",
    backgroundColor: "#00445D !important",
    color: "#FFFFFF !important",
    fontWeight: 600,
    textTransform: "none",
    fontSize: "14px",
    "&:hover": {
      backgroundColor: "#093642 !important",
      color: "#FFFFFF !important",
    },
    "&:disabled": {
      backgroundColor: "#B0BEC5 !important",
      color: "rgba(255, 255, 255, 0.7) !important",
    },
  },
  menuItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: "8px 16px",
  }
}));

export default function ManageAccessModal({ open, onClose, document, fileIdentifiers }) {
  const classes = useStyles();
  const [clientList, setClientList] = useState([]); // Array of {client, accessType}
  const [originalClientList, setOriginalClientList] = useState([]);
  const [fileName, setFileName] = useState("");
  const [fileIdentifier, setFileIdentifier] = useState("");
  const [backdrop, setBackdrop] = useState(false);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedClientIndex, setSelectedClientIndex] = useState(null);

  useEffect(() => {
    if (open && (document?.file_identifier || (fileIdentifiers && fileIdentifiers.length > 0))) {
      fetchSharedClientDetails();
    }
  }, [open, document, fileIdentifiers]);

  const fetchSharedClientDetails = async () => {
    setLoading(true);
    try {
      // Use the first file identifier if multiple files are selected
      const identifier = document?.file_identifier || fileIdentifiers[0];
      setFileIdentifier(identifier);
      
      const response = await DocuwareTemplateService.getSharedClientDetails(identifier);
      
      if (response.data.success) {
        const data = response.data.data;
        setFileName(data.fileName);
        
        // Convert to flat list with accessType
        const clients = [];
        const sharedWith = data.sharedWithClients || { edit: [], view: [] };
        
        // Add view clients first
        (sharedWith.view || []).forEach(client => {
          clients.push({ client, accessType: 'view' });
        });
        
        // Then add edit clients
        (sharedWith.edit || []).forEach(client => {
          clients.push({ client, accessType: 'edit' });
        });
        
        setClientList(clients);
        setOriginalClientList(JSON.parse(JSON.stringify(clients))); // Deep copy
      }
    } catch (error) {
      notify({
        type: "error",
        message: "Error fetching shared client details.",
      });
      console.error("Error fetching shared clients:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, index) => {
    setAnchorEl(event.currentTarget);
    setSelectedClientIndex(index);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedClientIndex(null);
  };

  const handleAccessChange = (newAccessType) => {
    if (selectedClientIndex === null) return;

    const updatedClients = [...clientList];
    
    // Update access type in place (including revoke)
    updatedClients[selectedClientIndex] = {
      ...updatedClients[selectedClientIndex],
      accessType: newAccessType
    };

    setClientList(updatedClients);
    handleMenuClose();
  };

  const handleUpdateAccess = async () => {
    setBackdrop(true);
    try {
      // Prepare the changes
      const addSharedClients = [];
      const removeSharedClients = [];

      // Create maps for easier lookup
      const originalMap = new Map();
      originalClientList.forEach(item => {
        originalMap.set(item.client, item.accessType);
      });

      const currentMap = new Map();
      clientList.forEach(item => {
        currentMap.set(item.client, item.accessType);
      });

      // Check for clients to add or update
      clientList.forEach(item => {
        const originalAccessType = originalMap.get(item.client);
        
        if (item.accessType === 'revoke') {
          // If revoked and was originally in the list, add to remove list
          if (originalAccessType && originalAccessType !== 'revoke') {
            removeSharedClients.push(item.client);
          }
        } else if (!originalAccessType || originalAccessType !== item.accessType) {
          // New client or access type changed (not revoke)
          addSharedClients.push({
            client: item.client,
            accessType: item.accessType,
          });
        }
      });

      // Check for clients that were removed from the list entirely
      originalClientList.forEach(item => {
        if (!currentMap.has(item.client)) {
          removeSharedClients.push(item.client);
        }
      });

      // Use fileIdentifiers if available, otherwise use single document identifier
      const identifiers = fileIdentifiers && fileIdentifiers.length > 0 
        ? fileIdentifiers 
        : [fileIdentifier];

      await DocuwareTemplateService.shareFiles(
        identifiers,
        addSharedClients,
        removeSharedClients
      );

      setBackdrop(false);
      notify({
        type: "success",
        message: "Access updated successfully.",
      });
      
      // Update original state to reflect changes
      setOriginalClientList(JSON.parse(JSON.stringify(clientList)));
      onClose();
    } catch (error) {
      setBackdrop(false);
      notify({
        type: "error",
        message: "Error updating access.",
      });
      console.error("Error updating access:", error);
    }
  };

  const getTotalTenantsCount = () => {
    return clientList.filter(item => item.accessType !== 'revoke').length;
  };

  const hasChanges = () => {
    return JSON.stringify(clientList) !== JSON.stringify(originalClientList);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      classes={{ paper: classes.drawerPaper }}
    >
      <Backdrop open={backdrop} style={{ color: "primary", zIndex: 1000000 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Title Bar */}
      <Box className={classes.titleBar}>
        <Box display="flex" alignItems="center">
          <IconButton onClick={onClose} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Box ml={1}>
            <Typography variant="h6">
              <b>Manage Access</b>
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {fileIdentifiers && fileIdentifiers.length > 0 
                ? `${fileIdentifiers.length} files` 
                : fileName || document?.name || "No document selected"}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      {/* Content */}
      <Box className={classes.content}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="body2" className={classes.section}>
              Tenants with access:{" "}
              <b style={{ 
                border: "1px solid #999", 
                borderRadius: 4, 
                padding: "2px 6px", 
                marginLeft: 6 
              }}>
                {getTotalTenantsCount()}
              </b>
            </Typography>

            {/* All Clients with Access */}
            {clientList.map((item, index) => (
              <Box key={`${item.client}-${index}`} className={classes.tenantItem}>
                <Typography variant="body2">{item.client}</Typography>
                <Box className={classes.accessButtonContainer}>
                  <Box
                    className={`${classes.iconButton} ${
                      anchorEl && selectedClientIndex === index ? classes.iconButtonActive : ""
                    }`}
                  >
                    {item.accessType === "view" ? (
                      <VisibilityIcon style={{ fontSize: 20, color: "#666" }} />
                    ) : item.accessType === "edit" ? (
                      <EditIcon style={{ fontSize: 20, color: "#666" }} />
                    ) : (
                      <LockIcon style={{ fontSize: 20, color: "#666" }} />
                    )}
                  </Box>
                  <IconButton
                    className={classes.dropdownButton}
                    onClick={(e) => handleMenuOpen(e, index)}
                    size="small"
                  >
                    {anchorEl && selectedClientIndex === index ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>
              </Box>
            ))}

            {getTotalTenantsCount() === 0 && (
              <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                minHeight="200px"
                flexDirection="column"
              >
                <Typography variant="body2" color="textSecondary">
                  No tenants have access to this document.
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Access Type Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={() => handleAccessChange("view")} className={classes.menuItem}>
          <VisibilityIcon className={classes.viewIcon} fontSize="small" />
          View
        </MenuItem>
        <MenuItem onClick={() => handleAccessChange("edit")} className={classes.menuItem}>
          <EditIcon className={classes.editIcon} fontSize="small" />
          Edit
        </MenuItem>
        <MenuItem onClick={() => handleAccessChange("revoke")} className={classes.menuItem}>
          <LockIcon className={classes.revokeIcon} fontSize="small" />
          Revoke Access
        </MenuItem>
      </Menu>

      {/* Footer */}
      <Box className={classes.footer}>
        <Button 
          variant="contained"
          onClick={handleUpdateAccess} 
          className={classes.updateBtn} 
          disabled={!hasChanges() || loading}
          disableElevation
        >
          Update Access
        </Button>
      </Box>
    </Drawer>
  );
}
